import { useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";

import { marketTopologyKey } from "@/hooks/useMarketIntelligence";
import { MARKET_PINS_KEY } from "@/hooks/useMarketZonePrefs";
import { queryClient } from "@/lib/query";

import {
  MI_GAS_PATH,
  MI_GAS_REGIONS_GROUP_PATH,
  MI_REGIONS_GROUP_PATH,
  regionSlug,
} from "./paths";
import { POINT_GROUPS } from "./point-groups";
import {
  GAS_REGIONS,
  isoHasRecs,
  gasHubs,
  hubsForIso,
  isoFromSlug,
  isoHasCapacity,
} from "./topology";

import type { ZoneRef } from "@/types/market-zone-prefs";
import type {
  MarketCommodity,
  MarketHub,
  MarketRegion,
  MarketTopology,
  PointKindCode,
} from "@/types/market-intelligence";

/**
 * Re-renders the caller whenever the topology query cache changes.
 *
 * `useSyncExternalStore` over the query cache rather than `useQuery`, so nav
 * code that renders outside a `QueryClientProvider` keeps working — several
 * tests do exactly that, because this hook needed no provider at all before
 * topology became a fetch. Returns the cached value so the subscription cannot
 * be optimised away as a bare side effect.
 */
function useTopologyRevision(): MarketTopology | undefined {
  // Subscribes to the whole cache, but the SNAPSHOT is what decides whether
  // React re-renders — `useSyncExternalStore` bails out when it is unchanged
  // by reference. So the snapshot has to cover everything the nav rows read,
  // not only the one they were originally built from: `regionHubs` now also
  // reads the pins cache to order itself, and a pin toggled elsewhere has to
  // move the sidebar.
  //
  // `getSnapshot` must be REFERENTIALLY stable while nothing has changed —
  // returning a fresh `[topo, pins]` tuple each call would loop forever. So it
  // returns the topology object, and a separate subscription below watches the
  // pins key. Two hooks, each with a stable snapshot, rather than one with a
  // synthetic one.
  const topology = useSyncExternalStore(
    (onChange) => queryClient.getQueryCache().subscribe(onChange),
    () => queryClient.getQueryData<MarketTopology>(marketTopologyKey()),
  );

  // The pins cache, watched only so a pin re-renders the nav. The value is
  // discarded — `regionHubs` reads it itself — but the
  // subscription is what makes the sidebar reorder the moment a card is
  // pinned, rather than on the next navigation.
  useSyncExternalStore(
    (onChange) => queryClient.getQueryCache().subscribe(onChange),
    () => queryClient.getQueryData<ZoneRef[]>(MARKET_PINS_KEY),
  );

  return topology;
}

/**
 * Which region the sidebar is scoped to.
 *
 * Modelled on Energy Advisor's client scope, but much simpler: a region is
 * static reference data, so there is no fetch, no error path, and no sticky
 * store to reconcile. The scope is read straight off the URL.
 *
 * Every scoped URL carries the region, hub pages included:
 *   /market-intelligence/electric/regions/pjm/recs
 *   /market-intelligence/electric/regions/pjm/hubs/pjm_west/forwards
 *
 * That is why hubs were nested under regions. The flat `/hubs/:hub` form
 * made this function infer the region from the hub, which worked only while
 * every hub belonged to an ISO — a gas hub carries a region name instead, and
 * silently dropped the scope.
 *
 * Returns `null` off those routes — the Overview has no region in scope, and
 * the picker shows its placeholder.
 */
export function useScopedRegion(): { id: string; label: string } | null {
  const { pathname } = useLocation();

  // Re-render when topology lands, without requiring a QueryClientProvider.
  //
  // `expandRows` on a `RecordScope` is a plain function and cannot call a
  // hook, so the Hubs row's children are built by `regionHubs` reading the
  // topology query's CACHE — and something has to re-render the sidebar when
  // that cache fills, or the nav keeps whatever the first paint found (the
  // static fallback).
  //
  // Subscribing to the query CACHE rather than calling `useMarketTopology()`
  // is deliberate: this hook is reached from nav code that renders outside any
  // provider in tests, and `useQuery` would make a previously fetch-free
  // function throw there. The cache singleton has no such requirement, and the
  // subscription is cheap — it fires on any query change and re-reads one key.
  useTopologyRevision();

  // Derived from `MI_REGIONS_GROUP_PATH`, never spelled out. A hardcoded
  // `/market-intelligence/regions/…` literal lived here and silently stopped
  // matching the moment the url gained its `/electric` commodity segment —
  // the sidebar simply lost its region scope, with nothing to point at the
  // cause. Every url in this module is built in `paths.ts`; every url PARSED
  // should be read off the same source.
  const prefix = `${MI_REGIONS_GROUP_PATH}/`;
  if (pathname.startsWith(prefix)) {
    const slug = pathname.slice(prefix.length).split("/")[0];
    const code = slug ? isoFromSlug(slug) : undefined;
    // The short code IS the label: "CAISO" reads faster than "California ISO"
    // and is what the desk actually says.
    if (code) return { id: code, label: code };
  }

  return null;
}

/**
 * The gas regions, A–Z: the topology's, or the static fallback before it lands.
 *
 * Read from the CACHE for the same reason `regionTopologyHubs` is: the nav
 * calls it during render, from plain functions.
 */
export function gasRegions(): readonly MarketRegion[] {
  const cached = queryClient.getQueryData<MarketTopology>(marketTopologyKey());
  return cached?.gasRegions ?? GAS_REGIONS;
}

/** A gas region from its url slug ("louisiana-southeast" → Louisiana/Southeast). */
export function gasRegionFromSlug(slug: string): MarketRegion | undefined {
  return gasRegions().find((r) => regionSlug(r.code) === slug.toLowerCase());
}

/**
 * Which gas region the sidebar is scoped to — `useScopedRegion`'s gas
 * counterpart, read the same way: off the url, which carries the region on
 * every gas page below the Overview.
 *
 *   /market-intelligence/gas/regions/northeast/delivery-points
 *   /market-intelligence/gas/regions/northeast/hubs/algonquin_cg/forwards
 *
 * The id and label are the region's code ("Northeast"), which is what its
 * points carry in `iso`.
 */
export function useScopedGasRegion(): { id: string; label: string } | null {
  const { pathname } = useLocation();
  useTopologyRevision();

  const prefix = `${MI_GAS_REGIONS_GROUP_PATH}/`;
  if (!pathname.startsWith(prefix)) return null;
  const slug = pathname.slice(prefix.length).split("/")[0];
  const region = slug ? gasRegionFromSlug(slug) : undefined;
  return region ? { id: region.code, label: region.code } : null;
}

/**
 * The pricing points the sidebar lists under a region: every one of them,
 * hubs first, then load zones (or, for gas, delivery points), each block A–Z.
 *
 * **Every point is top level.** Load zones used to be reached by drilling
 * into the hub they price against, as tabs on its page. The business found
 * that hid them, so every point is its own row and page again (2026-10-01),
 * and the parent survives only as the Basis reference.
 *
 * Grouped by kind rather than strictly A–Z, the one exception to the house
 * rule that a nav list is alphabetical, because the region overview groups its
 * cards the same way (Hubs, then Load zones). The sidebar's order matches the
 * order of the cards it links to. The rows carry the same kind icons
 * (`PointKind.tsx`), so the grouping reads without a heading this depth has
 * no slot for.
 *
 * Pinned points lead WITHIN their block, never across it, so a pinned load
 * zone does not sit among the hubs. Same rule the region page's cards apply.
 */
export function regionHubs(iso: string, commodity: MarketCommodity = "power"): MarketHub[] {
  const pinned = pinnedCodes(commodity);
  return [...regionTopologyHubs(iso, commodity)].sort((a, b) => {
    const kind = (h: MarketHub) => (h.kind === "hub" ? 0 : 1);
    const pin = (h: MarketHub) => (pinned.has(h.code) ? 0 : 1);
    return kind(a) - kind(b) || pin(a) - pin(b) || a.name.localeCompare(b.name);
  });
}

/**
 * Every point of one commodity in a region, as the topology knows it.
 *
 * Read from the topology query's CACHE rather than a hook, because the callers
 * — `RecordScope.expandRows` — are plain functions the registry calls during
 * render. `useScopedRegion` subscribes to the same query, so the sidebar
 * re-renders when it lands and this picks the answer up.
 *
 * Falling back to the static constant covers two cases that must both work:
 * the first paint before topology resolves, and the seeded generator, which
 * answers no such request. The constant is wrong about the feed — it lists six
 * PJM hubs where the feed publishes seventeen, and names a `West` the feed does
 * not publish at all — so this fallback is a brief wrong answer, never a
 * resting state.
 */
export function regionTopologyHubs(
  iso: string,
  commodity: MarketCommodity = "power",
): MarketHub[] {
  const cached = queryClient.getQueryData<MarketTopology>(marketTopologyKey());
  const fallback =
    commodity === "gas" ? gasHubs().filter((h) => h.iso === iso) : hubsForIso(iso);
  return (
    cached?.hubs.filter((h) => h.commodity === commodity && h.iso === iso) ?? fallback
  );
}

/**
 * The codes this reader pinned, for one commodity.
 *
 * Reads the pins query's CACHE rather than calling a hook, because the callers
 * here are plain functions the nav registry invokes during render — the same
 * constraint `regionHubs` documents for topology. `useScopedRegion` and
 * `useScopedGasRegion` already subscribe to the cache, so a pin lands in the
 * sidebar without either of them knowing about pins specifically.
 *
 * An empty set when nothing is pinned or the query has not run, which sorts
 * to "no preference" rather than to an error.
 */
export function pinnedCodes(commodity: MarketCommodity): Set<string> {
  const pins = queryClient.getQueryData<ZoneRef[]>(MARKET_PINS_KEY);
  return new Set(
    (Array.isArray(pins) ? pins : [])
      .filter((p) => p.commodity === commodity)
      .map((p) => p.code),
  );
}

/**
 * Which of a region's surfaces exist for it.
 *
 * Not every market has every surface: capacity needs a centralised auction
 * (`CAPACITY_ISOS`, PJM alone today) and RECs need a certificate program.
 * MISO and SPP have neither.
 *
 * Lives here so the sidebar rows and the region page's tab strip apply the
 * SAME rule. They were computed separately, which meant the sidebar advertised
 * a RECs link for MISO that led to a tab the page had already dropped.
 *
 * Keyed by the tab's path segment, which is what both callers hold.
 */
export function regionHasSurface(iso: string, segment: string): boolean {
  if (segment === "hubs") return regionPointsOf(iso, "hub").length > 0;
  if (segment === "load-zones") return regionPointsOf(iso, "load_zone").length > 0;
  return regionRoutesSurface(iso, segment);
}

/**
 * `regionHasSurface` for a gas region: its Hubs and Delivery points rows,
 * each only when the region has one of that kind — the Northeast has no
 * supply hub, as NYISO has no power hub. Gas has no RECs or Capacity.
 */
export function gasRegionHasSurface(region: string, segment: string): boolean {
  const group = POINT_GROUPS.gas.find((g) => g.tab === segment);
  return group ? regionPointsOf(region, group.kind, "gas").length > 0 : false;
}

/**
 * Whether a region's surface gets a ROUTE: `regionHasSurface` minus the
 * point-kind checks.
 *
 * Routes are registered once, at module load, before topology is fetched, so
 * Hubs and Load zones are registered for every region. The sidebar row is what
 * the live topology gates (`regionHasSurface`), so MISO shows no Load zones
 * row and NYISO no Hubs row, while a link to either still resolves to the
 * overview's own empty state rather than a 404.
 */
export function regionRoutesSurface(iso: string, segment: string): boolean {
  if (segment === "capacity") return isoHasCapacity(iso);
  if (segment === "recs") return isoHasRecs(iso);
  return true;
}

/**
 * One kind of the region's points, in `regionHubs`' order: what the sidebar's
 * Hubs and Load zones (or Delivery points) rows each expand into. Every point
 * that is not a hub falls in the other group here, hedgeable or not.
 */
export function regionPointsOf(
  iso: string,
  kind: PointKindCode,
  commodity: MarketCommodity = "power",
): MarketHub[] {
  return regionHubs(iso, commodity).filter(
    (h) => (kind === "hub") === (h.kind === "hub"),
  );
}

/**
 * Whether a url is on gas's side of the Electric/Gas split: anything under
 * the gas root (`MI_GAS_PATH`), its Overview included.
 *
 * The one predicate `useMarketCommodityScope` and `ModuleSection.visibleWhen`
 * both key off (the module's `mi-region`/`mi-gas` sections invert it against
 * each other), so the sidebar toggle, the section swap, and the Overview page
 * can never disagree about which commodity is showing. A plain function
 * rather than a hook so it can also serve `visibleWhen`, which is
 * deliberately not one — see that field's doc comment.
 */
export function isGasScoped({ pathname }: { pathname: string }): boolean {
  return pathname === MI_GAS_PATH || pathname.startsWith(`${MI_GAS_PATH}/`);
}

/**
 * Which commodity the sidebar's Electric/Gas toggle should show as selected.
 *
 * Derived from the url rather than kept as separate state, so it can never
 * disagree with the page underneath it — see `isGasScoped`.
 */
export function useMarketCommodityScope(): MarketCommodity {
  const { pathname } = useLocation();
  return isGasScoped({ pathname }) ? "gas" : "power";
}
